import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Autocomplete,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Box,
  Typography,
  useTheme,
} from '@mui/material';

import {
  normalizeUsername,
  isValidUsername,
  dedupeKeywords,
  MATCH_MODES,
  SUBSTRING_CAVEAT,
  WORD_BOUNDARY_CAVEAT,
  MAX_RESULTS_HELP,
  KEYWORD_SOFT_WARN,
  KEYWORD_SOFT_WARN_COPY,
  KEYWORD_HARD_CAP,
  ACTIVE_NEEDS_KEYWORDS_COPY,
  MAX_RESULTS_MIN,
  MAX_RESULTS_MAX,
  DISPLAY_NAME_MAX,
} from '../../../utils/twitterValidation';

const DEFAULT_MAX_RESULTS = 20;

const DEFAULTS = {
  username: '',
  display_name: '',
  keywords: [],
  match_mode: 'word_boundary',
  max_results: String(DEFAULT_MAX_RESULTS),
  active: true,
};

export default function TwitterAccountDialog({
  open,
  mode = 'add', // 'add' | 'edit'
  initialValues,
  onClose,
  onSubmit,
}) {
  const theme = useTheme();
  const [values, setValues] = useState(DEFAULTS);
  const [submitError, setSubmitError] = useState(null);
  const [saving, setSaving] = useState(false);

  const greenSx = {
    textTransform: 'none',
    fontWeight: 600,
    backgroundColor: theme.palette.success.main,
    '&:hover': { backgroundColor: theme.palette.success.dark },
  };

  useEffect(() => {
    if (!open) return;
    if (mode === 'edit' && initialValues) {
      setValues({
        username: initialValues.username || '',
        display_name: initialValues.display_name || '',
        keywords: Array.isArray(initialValues.keywords) ? initialValues.keywords : [],
        match_mode: initialValues.match_mode || 'word_boundary',
        max_results:
          initialValues.max_results == null ? '' : String(initialValues.max_results),
        active: Boolean(initialValues.active),
      });
    } else {
      setValues(DEFAULTS);
    }
    setSubmitError(null);
  }, [open, mode, initialValues]);

  const isEdit = mode === 'edit';

  const dedupedKeywords = useMemo(
    () => dedupeKeywords(values.keywords),
    [values.keywords],
  );

  const usernameNormalized = useMemo(
    () => normalizeUsername(values.username),
    [values.username],
  );

  const errors = useMemo(() => {
    const e = {};
    if (!isEdit) {
      if (!usernameNormalized) e.username = 'Required';
      else if (!isValidUsername(usernameNormalized))
        e.username = 'Letters, numbers, underscore. 1–15 chars.';
    }
    if (!values.display_name.trim()) e.display_name = 'Required';
    else if (values.display_name.length > DISPLAY_NAME_MAX)
      e.display_name = `Max ${DISPLAY_NAME_MAX} chars.`;
    if (dedupedKeywords.length > KEYWORD_HARD_CAP)
      e.keywords = `Maximum ${KEYWORD_HARD_CAP} keywords.`;
    if (values.active && dedupedKeywords.length === 0)
      e.active = ACTIVE_NEEDS_KEYWORDS_COPY;
    if (values.max_results !== '') {
      const n = Number(values.max_results);
      if (!Number.isInteger(n) || n < MAX_RESULTS_MIN || n > MAX_RESULTS_MAX)
        e.max_results = `Integer in [${MAX_RESULTS_MIN}, ${MAX_RESULTS_MAX}].`;
    }
    return e;
  }, [isEdit, usernameNormalized, values, dedupedKeywords]);

  const hasErrors = Object.keys(errors).length > 0;

  const handleSubmit = async () => {
    if (hasErrors) return;
    setSaving(true);
    setSubmitError(null);
    try {
      const payload = {
        display_name: values.display_name.trim(),
        keywords: dedupedKeywords,
        match_mode: values.match_mode,
        active: values.active,
        max_results:
          values.max_results === '' ? DEFAULT_MAX_RESULTS : Number(values.max_results),
      };
      if (!isEdit) payload.username = usernameNormalized;

      await onSubmit(payload, { mode, originalUsername: initialValues?.username });
      onClose();
    } catch (err) {
      setSubmitError(err?.message || String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? `Edit @${values.username}` : 'Add Twitter Account'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {submitError && <Alert severity="error" onClose={() => setSubmitError(null)}>{submitError}</Alert>}

          <TextField
            label="Username"
            value={values.username}
            onChange={(e) => setValues((v) => ({ ...v, username: e.target.value }))}
            onBlur={() =>
              setValues((v) => ({ ...v, username: normalizeUsername(v.username) }))
            }
            disabled={isEdit}
            error={Boolean(errors.username)}
            helperText={
              errors.username || (isEdit ? 'Username is immutable. Delete + recreate to rename.' : 'Without leading @.')
            }
            fullWidth
            size="small"
          />

          <TextField
            label="Display name"
            value={values.display_name}
            onChange={(e) => setValues((v) => ({ ...v, display_name: e.target.value }))}
            error={Boolean(errors.display_name)}
            helperText={errors.display_name || `${values.display_name.length}/${DISPLAY_NAME_MAX}`}
            fullWidth
            size="small"
            inputProps={{ maxLength: DISPLAY_NAME_MAX }}
          />

          <Box>
            <Autocomplete
              multiple
              freeSolo
              size="small"
              options={[]}
              value={values.keywords}
              onChange={(_, next) => setValues((v) => ({ ...v, keywords: next }))}
              renderTags={(tags, getTagProps) =>
                tags.map((opt, idx) => (
                  <Chip
                    {...getTagProps({ index: idx })}
                    key={`${opt}-${idx}`}
                    label={opt}
                    size="small"
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Keywords"
                  placeholder="Press Enter to add"
                  error={Boolean(errors.keywords)}
                />
              )}
            />
            <FormHelperText error={Boolean(errors.keywords)}>
              {errors.keywords ||
                `${dedupedKeywords.length} keyword${dedupedKeywords.length === 1 ? '' : 's'}` +
                  (dedupedKeywords.length > KEYWORD_SOFT_WARN
                    ? ` — ${KEYWORD_SOFT_WARN_COPY}`
                    : '')}
            </FormHelperText>
          </Box>

          <FormControl size="small" fullWidth>
            <InputLabel id="match-mode-label">Match mode</InputLabel>
            <Select
              labelId="match-mode-label"
              label="Match mode"
              value={values.match_mode}
              onChange={(e) => setValues((v) => ({ ...v, match_mode: e.target.value }))}
            >
              {MATCH_MODES.map((m) => (
                <MenuItem key={m.value} value={m.value}>
                  {m.label}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              {values.match_mode === 'substring' ? SUBSTRING_CAVEAT : WORD_BOUNDARY_CAVEAT}
            </FormHelperText>
          </FormControl>

          <TextField
            label="Max results (optional)"
            type="number"
            value={values.max_results}
            onChange={(e) => setValues((v) => ({ ...v, max_results: e.target.value }))}
            error={Boolean(errors.max_results)}
            helperText={
              errors.max_results ||
              `${MAX_RESULTS_HELP} Integer ${MAX_RESULTS_MIN}–${MAX_RESULTS_MAX}; defaults to 20 if blank.`
            }
            inputProps={{ min: MAX_RESULTS_MIN, max: MAX_RESULTS_MAX, step: 1 }}
            size="small"
            fullWidth
          />

          <Box>
            <FormControlLabel
              control={
                <Switch
                  checked={values.active}
                  onChange={(e) => setValues((v) => ({ ...v, active: e.target.checked }))}
                />
              }
              label="Active"
            />
            {errors.active && (
              <Typography variant="caption" color="error" sx={{ display: 'block' }}>
                {errors.active}
              </Typography>
            )}
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={saving || hasErrors}
          sx={greenSx}
        >
          {saving ? <CircularProgress size={20} /> : isEdit ? 'Save' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
